import { GeometryTypes } from '../../common/utility/geometry-utils';
import { Style } from '../styles/style';
import { LayerSymbologyType, LineStyle, SymbologyOverrideChoice } from '../styles/style-symbology';

export const TrimbleColorPalette = {
    Red: '#D00A09',
    Orange: '#FD8204',
    Yellow: '#FFD600',
    LimeGreen: '#64AD22',
    ForestGreen: '#01863E',
    Teal: '#028986',
    RoyalBlue: '#0967AD',
    NavyBlue: '#3939AC',

    // extended
    Purple: '#773899',
    Pink: '#BD0760',

    Dark: {
        Red: '#990000',
        Orange: '#D64B02',
        Yellow: '#FFBE00',
        LimeGreen: '#367314',
        ForestGreen: '#004E18',
        Teal: '#01534F',
        RoyalBlue: '#083061',
        NavyBlue: '#1A1A52',

        // extended
        Purple: '#431860',
        Pink: '#77042C'
    },

    Bright: {
        Red: '#FF2722',
        Orange: '#FFB22E',
        Yellow: '#FFFA00',
        LimeGreen: '#B4FF00',
        ForestGreen: '#09C72E',
        Teal: '#0BDDD9',
        RoyalBlue: '#2B9FF6',
        NavyBlue: '#4747FF',

        // extended
        Purple: '#AA46E5',
        Pink: '#FF2D96'
    }
};

export const ColorPickerPresetColors: { color: string; title: string }[] = [
    // ngx-color does not support title/tooltip based on color name given here. Yet.
    { color: '#0E416C', title: 'Blue Dark' },
    { color: '#0063A3', title: 'Blue' },
    { color: '#217CBB', title: 'Blue Light' },
    { color: '#DCEDF9', title: 'Blue Pale' },
    { color: '#E49325', title: 'Yellow Dark' },
    { color: '#FBAD26', title: 'Yellow' },
    { color: '#FEC157', title: 'Yellow Light' },
    { color: '#FFF5E4', title: 'Yellow Pale' },
    { color: '#AB1F26', title: 'Red Dark' },
    { color: '#DA212C', title: 'Red' },
    { color: '#E86363', title: 'Red Light' },
    { color: '#FBD4D7', title: 'Red Pale' },
    { color: '#006638', title: 'Green Dark' },
    { color: '#1E8A44', title: 'Green' },
    { color: '#4EA646', title: 'Green Light' },
    { color: '#E0ECCF', title: 'Green Pale' },
    { color: '#5A2A7D', title: 'Purple Dark' },
    { color: '#7A3DAF', title: 'Purple' },
    { color: '#9B5FC0', title: 'Purple Light' },
    { color: '#EBD0FF', title: 'Purple Pale' },
    { color: '#000000', title: 'Black' },
    { color: '#FFFFFF', title: 'White' }
];

export class LayerStyleGenerator {
    constructor() {}

    // Model/Constructor
    static getStyles(n: number): Style[] {
        let styles: Style[] = [];
        let suportedColorSequences = [
            TrimbleColorPalette.Red,
            TrimbleColorPalette.LimeGreen,
            TrimbleColorPalette.RoyalBlue,
            TrimbleColorPalette.Yellow,
            TrimbleColorPalette.Teal,
            TrimbleColorPalette.Purple,
            TrimbleColorPalette.Orange,
            TrimbleColorPalette.ForestGreen,
            TrimbleColorPalette.Pink,
            TrimbleColorPalette.NavyBlue,
            TrimbleColorPalette.Dark.Red,
            TrimbleColorPalette.Dark.Orange,
            TrimbleColorPalette.Dark.Yellow,
            TrimbleColorPalette.Dark.LimeGreen,
            TrimbleColorPalette.Dark.ForestGreen,
            TrimbleColorPalette.Dark.Teal,
            TrimbleColorPalette.Dark.RoyalBlue,
            TrimbleColorPalette.Dark.NavyBlue,
            TrimbleColorPalette.Dark.Purple,
            TrimbleColorPalette.Dark.Pink,
            TrimbleColorPalette.Bright.Red,
            TrimbleColorPalette.Bright.Orange,
            TrimbleColorPalette.Bright.Yellow,
            TrimbleColorPalette.Bright.LimeGreen,
            TrimbleColorPalette.Bright.ForestGreen,
            TrimbleColorPalette.Bright.Teal,
            TrimbleColorPalette.Bright.RoyalBlue,
            TrimbleColorPalette.Bright.NavyBlue,
            TrimbleColorPalette.Bright.Purple,
            TrimbleColorPalette.Bright.Pink
        ];

        const colors = suportedColorSequences
            .map(x => ({ x, r: Math.random() }))
            .sort((a, b) => a.r - b.r)
            .map(a => a.x)
            .slice(0, n);

        colors.forEach(color => {
            let style = new Style();
            style.name = 'style' + color;
            style.fillColor = parseInt(color.replace('#', '').replace('0x', ''), 16);
            style.fillOpacity = 2;
            style.lineColor = parseInt(color.replace('#', '').replace('0x', ''), 16);
            style.lineStyle = LineStyle.SOLID;
            style.lineWeight = 5;
            style.image = '';

            styles.push(style);
        });
        return styles;
    }

    static getStyle(color: string): Style {
        let style = new Style();
        style.name = 'style' + color;
        style.fillColor = parseInt(color.replace('#', '').replace('0x', ''), 16);
        style.fillOpacity = 2;
        style.lineColor = parseInt(color.replace('#', '').replace('0x', ''), 16);
        style.lineStyle = LineStyle.SOLID;
        style.lineWeight = 5;
        style.image = '';
        return style;
    }

    static randomHexGenerator(): string {
        return (
            '#' +
            Math.floor(Math.random() * 16777215)
                .toString(16)
                .padStart(6, '0')
        );
    }

    static hexToDecimalRgb(hex: string): number {
        if (hex) {
            hex = hex.replace(/^#/, '');
            return parseInt(hex, 16);
        }
    }

    static decimalToHexRgb(decimal: number, skipHash = false): string {
        if (decimal != null) {
            const hex = decimal.toString(16).padStart(6, '0');
            return skipHash ? hex : '#' + hex;
        }
    }

    static getRandomColor(): number {
        const randomIndex = Math.floor(Math.random() * ColorPickerPresetColors.length);
        return this.hexToDecimalRgb(ColorPickerPresetColors[randomIndex].color);
    }

    // Symbology override styling

    static getOutlineColorFromStyle(style: Style, geometryType: GeometryTypes, fallback?: string): string {
        let outlineColor: string;

        if (geometryType === GeometryTypes.POINT) {
            outlineColor = LayerStyleGenerator.decimalToHexRgb(style.symbol?.outlineColor) || fallback;
        } else {
            outlineColor = LayerStyleGenerator.decimalToHexRgb(style.lineColor);
        }

        return outlineColor || fallback;
    }

    static getOutlineColorFromOverride(
        override: SymbologyOverrideChoice,
        geometryType: GeometryTypes,
        fallback?: string
    ): string {
        let outlineColor: string;

        if (geometryType === GeometryTypes.POINT || geometryType === GeometryTypes.LINE) {
            outlineColor = LayerStyleGenerator.decimalToHexRgb(override.outlineColor);
        } else {
            outlineColor = LayerStyleGenerator.decimalToHexRgb(override.lineColor);
        }

        return outlineColor || fallback;
    }

    static getFillColorFromOverride(
        override: SymbologyOverrideChoice,
        style: Style,
        geometryType: GeometryTypes,
        fallback?: string
    ): string {
        let fillColor: string;

        if (geometryType === GeometryTypes.POINT || geometryType === GeometryTypes.AREA) {
            if (style.symbol?.type === LayerSymbologyType.CUSTOMIZABLE) {
                fillColor = LayerStyleGenerator.decimalToHexRgb(override.color);
            } else {
                fillColor = LayerStyleGenerator.decimalToHexRgb(override.fillColor);
            }
        } else {
            fillColor = LayerStyleGenerator.decimalToHexRgb(override.lineColor);
        }

        return fillColor || fallback;
    }
}
